// League workspace — Executive Committee view.
//
// Landing page treatment:
//   • Dark gradient "Executive Leadership" hero
//   • Section intro banner ("Leadership Built on Service, Structure,
//     and Community")
//   • Rich member cards laid out on a 2-column grid — portrait on the
//     left, name / role / bio / key contributions on the right. Bios
//     and contributions are role-level (from ROLE_BIOS in ./shared.js)
//     so the copy stays evergreen as holders change season over season.
//   • Vacant-role callout, plus the RoleAssignmentsPanel below for
//     the admins who can manage assignments.

import React, { useEffect, useMemo, useState } from "react";
import { Landmark, CheckCircle2, ChevronRight } from "lucide-react";
import { Panel } from "../../components/primitives";
import { useToast } from "../../components/Toast";
import Avatar from "../../components/Avatar";
import RoleAssignmentsPanel from "../../components/RoleAssignmentsPanel";
import { api } from "../../api";
import { ROLE_META, ROLE_BIOS } from "./shared";

const ROLE_MGR_ROLES = ["ADMIN", "PRESIDENT", "VICE_PRESIDENT", "GENERAL_SECRETARY"];

// Fixed presentation order — mirrors the hierarchy on the reference
// page: exec officers first, then portfolio heads (rules, umpiring,
// digital), then the broader committee at the end.
const ROLE_ORDER = [
  "PRESIDENT", "VICE_PRESIDENT", "GENERAL_SECRETARY", "TREASURER",
  "RULES_HEAD", "UMPIRING_HEAD", "SOCIAL_MEDIA_ADMIN",
  "EXEC_COMMITTEE_MEMBER"
];

export default function LeagueCommittee({ user, state }) {
  const toast = useToast();
  const [committee, setCommittee] = useState([]);
  const [loading,   setLoading]   = useState(true);

  const canManageRoles = (user?.roles || []).some(r => ROLE_MGR_ROLES.includes(r));

  async function refresh() {
    setLoading(true);
    try {
      const c = await api.listCommittee();
      setCommittee(c);
    } catch (err) {
      toast.error(err.message || "Couldn't load committee.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const leagueOnly = useMemo(() => committee.filter(r => r.code !== "ADMIN"), [committee]);
  const vacantCodes = leagueOnly.filter(r => r.holders.length === 0).map(r => r.code);

  // Flatten roles → cards.
  //   • Single-seat roles (President … Social Media Admin) always emit
  //     exactly one card — with the holder if assigned, or a
  //     "Position vacant" placeholder so the leadership structure is
  //     visible even before appointments are made.
  //   • Multi-seat roles (Committee Members) emit one card per holder
  //     and nothing when vacant — a page full of duplicate placeholders
  //     for the same role would add no signal.
  const cards = useMemo(() => {
    const byCode = Object.fromEntries(leagueOnly.map(r => [r.code, r]));
    const MULTI  = new Set(["EXEC_COMMITTEE_MEMBER"]);
    const out = [];
    for (const code of ROLE_ORDER) {
      const role = byCode[code];
      const meta = ROLE_META[code];
      const bio  = ROLE_BIOS[code];
      if (!meta || !bio) continue;
      const holders = role?.holders || [];
      if (holders.length === 0 && !MULTI.has(code)) {
        out.push({ holder: null, meta, bio, code });
        continue;
      }
      for (const holder of holders) {
        out.push({ holder, meta, bio, code });
      }
    }
    return out;
  }, [leagueOnly]);

  return (
    <div className="stack league-view">
      {/* ── Hero ──────────────────────────────────────────────── */}
      <header className="exec-hero">
        <div className="exec-hero-orb" aria-hidden="true"/>
        <div className="exec-hero-inner">
          <span className="exec-hero-chip">Pittsburgh Cricket League</span>
          <h1>Executive Leadership</h1>
          <p>
            A committed leadership team focused on governance, transparency, fair play,
            operational excellence, and the long-term growth of cricket in Pittsburgh.
          </p>
        </div>
      </header>

      {/* ── Intro banner ──────────────────────────────────────── */}
      <section className="exec-intro">
        <h2>Leadership Built on Service, Structure, and Community</h2>
        <p>
          The PCL Executive Leadership team brings together experienced administrators,
          players, and volunteers who support the league's continued evolution as a
          competitive, professional, and community-driven cricket organisation.
        </p>
      </section>

      {loading && (
        <p style={{color:"var(--muted)"}}>Loading committee…</p>
      )}

      {!loading && cards.length === 0 && (
        <div className="league-annEmpty">
          <Landmark size={28}/>
          <p>No committee members assigned yet. Use Role Management below to assign leadership seats.</p>
        </div>
      )}

      {/* ── Member cards grid ────────────────────────────────── */}
      {!loading && cards.length > 0 && (
        <div className="exec-grid">
          {cards.map(({ holder, meta, bio, code }, idx) => (
            <ExecCard
              key={holder ? `${code}:${holder.id}` : `${code}:vacant:${idx}`}
              holder={holder}
              meta={meta}
              bio={bio}
            />
          ))}
        </div>
      )}

      {/* ── Committee Members vacant callout ──────────────────── */}
      {!loading && vacantCodes.includes("EXEC_COMMITTEE_MEMBER") && (
        <div className="exec-vacant">
          <div className="exec-vacant-body">
            <b>Committee Members</b>
            <span>
              No general committee members appointed yet
            </span>
          </div>
          {canManageRoles && (
            <span className="exec-vacant-hint">
              Assign below <ChevronRight size={13}/>
            </span>
          )}
        </div>
      )}

      {canManageRoles && <RoleAssignmentsPanel state={state}/>}
    </div>
  );
}

// ── Exec member card ─────────────────────────────────────────────
// Portrait area on the left (solid tone-color background with the
// member's Avatar rendered as a rounded rectangle), content on the
// right with eyebrow, name, role, bio, and Key Contributions list.
// Vacant seats render the same shell with a dashed "Position Vacant"
// portrait placeholder so the leadership structure stays visible.
function ExecCard({ holder, meta, bio }) {
  const Icon = meta.Icon;
  const isVacant = !holder;
  const displayName = holder
    ? (holder.name || `${holder.firstName || ""} ${holder.lastName || ""}`.trim() || "—")
    : "Position Vacant";
  return (
    <article className={`exec-card exec-card--${meta.tone}${isVacant ? " exec-card--vacant" : ""}`}>
      <div className="exec-card-strip" aria-hidden="true"/>
      <div className="exec-card-portrait">
        {isVacant ? (
          <div
            className="exec-card-portraitVacant"
            style={{ width: 140, height: 170 }}
            aria-label="Position vacant"
          >
            <Icon size={44}/>
          </div>
        ) : (
          <Avatar
            member={holder}
            size={140}
            style={{
              width: 140, height: 170,
              borderRadius: 14,
              fontSize: 44
            }}
          />
        )}
      </div>
      <div className="exec-card-body">
        <span className="exec-card-eyebrow">PCL Executive Leadership</span>
        <h3 className="exec-card-name">{displayName}</h3>
        <span className={`exec-card-role exec-card-role--${meta.tone}`}>
          <Icon size={13}/> {bio.title}
        </span>
        <p className="exec-card-bio">{bio.bio}</p>
        <div className="exec-card-kc">
          <div className="exec-card-kc-head">Key Contributions</div>
          <ul>
            {bio.contributions.map((c, i) => (
              <li key={i}>
                <CheckCircle2 size={12}/>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </article>
  );
}
